import React, { type SyntheticEvent, useCallback, useEffect, useState } from 'react';

import { useIntl } from 'react-intl-next';

import {
	type UIAnalyticsEvent,
	useAnalyticsEvents as useAllAnalyticsEvents,
} from '@atlaskit/analytics-next';
import TeamsIcon from '@atlaskit/icon/core/teams';
import { fg } from '@atlaskit/platform-feature-flags';
import Popup, { type TriggerProps } from '@atlaskit/popup';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Pressable, xcss } from '@atlaskit/primitives';
import { useAnalyticsEvents } from '@atlaskit/teams-app-internal-analytics';
import { useConnectedTeams } from '@atlaskit/teams-public';
import { usePeopleTeamAnalyticsEvents } from '@atlassian/people-teams-ui/analytics';

import PopupComponent from '../../common/no-background-popup-component';
import { isAnalyticsEventsMigrated } from '../../common/utils/statsig';
import { TeamSelector } from '../team-selector';

import messages from './messages';

type TeamButtonProps = {
	containerId: string; // TODO: ARI type?
	Wrapper?: React.ElementType;
};

const buttonStyles = xcss({
	backgroundColor: 'color.background.neutral.subtle',
	borderWidth: 'border.width',
	borderStyle: 'solid',
	borderColor: 'color.border',
	borderRadius: 'radius.small',
	paddingInline: 'space.075',
	display: 'flex',
	alignItems: 'center',
	height: '32px',
	color: 'color.text.subtle',

	':hover': {
		backgroundColor: 'color.background.neutral.subtle.hovered',
	},
	':active': {
		backgroundColor: 'color.background.neutral.subtle.pressed',
	},
});

export const TeamButton = ({ containerId, Wrapper = React.Fragment }: TeamButtonProps) => {
	const {
		numberOfTeams,
		teams,
		isLoading,
		hasLoaded,
		containerId: currentContainerId,
		fetchNumberOfConnectedTeams,
		fetchConnectedTeams,
	} = useConnectedTeams();
	// NOTE: Later add loading state when button shows always
	const [isOpen, setIsOpen] = useState(false);
	const { formatMessage } = useIntl();
	const { fireEvent: DEPRECATED_fireEvent } = usePeopleTeamAnalyticsEvents();
	const { fireEvent } = useAnalyticsEvents();
	const { createAnalyticsEvent } = useAllAnalyticsEvents();

	useEffect(() => {
		// if containerId is changed, we need to fetch the teams again
		if (currentContainerId === undefined || containerId !== currentContainerId) {
			fetchNumberOfConnectedTeams(containerId);
		}
	}, [containerId, fetchNumberOfConnectedTeams, currentContainerId]);

	useEffect(() => {
		if (containerId === currentContainerId && numberOfTeams !== undefined && numberOfTeams > 0) {
			const result = createAnalyticsEvent({})?.context.find(
				(item) => item.attributes && item.attributes.scope !== undefined,
			);
			if (isAnalyticsEventsMigrated()) {
				fireEvent('ui.teamButton.viewed', {
					teamsCount: numberOfTeams ?? 0,
					scope: result?.attributes?.scope ?? '',
					version: 'm1',
				});
			} else {
				DEPRECATED_fireEvent('ui.teamButton.viewed', {
					teamsCount: numberOfTeams ?? 0,
					scope: result?.attributes?.scope ?? '',
					version: 'm1',
				});
			}
		}
	}, [
		containerId,
		createAnalyticsEvent,
		currentContainerId,
		fireEvent,
		DEPRECATED_fireEvent,
		numberOfTeams,
	]);

	const onClick = useCallback(
		(_: SyntheticEvent, analyticsEvent: UIAnalyticsEvent) => {
			setIsOpen(true);

			const result = analyticsEvent?.context.find(
				(item) => item.attributes && item.attributes.scope !== undefined,
			);

			if (isAnalyticsEventsMigrated()) {
				fireEvent('ui.teamButton.clicked', {
					teamsCount: numberOfTeams ?? 0,
					scope: result?.attributes?.scope ?? '',
					version: 'm1',
				});
			} else {
				DEPRECATED_fireEvent('ui.teamButton.clicked', {
					teamsCount: numberOfTeams ?? 0,
					scope: result?.attributes?.scope ?? '',
					version: 'm1',
				});
			}

			if (!teams && !isLoading && !hasLoaded) {
				fetchConnectedTeams(containerId);
			}
		},
		[
			containerId,
			fetchConnectedTeams,
			isLoading,
			teams,
			hasLoaded,
			fireEvent,
			DEPRECATED_fireEvent,
			numberOfTeams,
		],
	);

	if (containerId !== currentContainerId || numberOfTeams === undefined || numberOfTeams === 0) {
		return null;
	}

	return (
		<Popup
			isOpen={isOpen}
			testId="team-button-popup"
			onClose={() => setIsOpen(false)}
			content={() => <TeamSelector containerId={containerId} teams={teams} />}
			trigger={(triggerProps: TriggerProps) => (
				<Wrapper>
					<Pressable
						{...triggerProps}
						onClick={onClick}
						xcss={buttonStyles}
						testId="team-button-test-id"
					>
						<TeamsIcon color="currentColor" label={formatMessage(messages.iconLabel)} />
						{numberOfTeams > 1 && <Box padding="space.050">{numberOfTeams}</Box>}
					</Pressable>
				</Wrapper>
			)}
			placement="bottom-start"
			strategy="absolute"
			popupComponent={PopupComponent}
			shouldRenderToParent={fg('should-render-to-parent-should-be-true-people-and-')}
		/>
	);
};
